<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import UIKit from '@pocketprep/ui-kit'
import { groupModule } from '@/store/group/module'
import { studentModule } from '@/store/student/module'
import { statsModule } from '@/store/stats/module'
import GroupSubjectRow from '@/components/Stats/GroupStats/GroupSubjectRow.vue'
import { analyticsModule } from '@/store/analytics/module'

export type TSubjectRow = {
    id: number
    subject: string
    numFlaggedSubject: number
    tabIndex: number
}

const {
    Table,
    Button: PocketButton,
    EmptyState,
} = UIKit

const subjectTableColumns = [{
    name: 'Subject',
    propName: 'subject',
}, {
    name: 'Flagged',
    propName: 'numStudentFlagged',
}]

const groupFlaggedQuestions = computed(() => {
    return statsModule.getters.getCurrentGroupAggStats().questionStats
        .filter(q => !q.isMockQuestion && q.numStudentsFlagged > 0)
})

const flaggedGroupSubjects = computed(() => {
    const flaggedSubjects: TSubjectRow[] = []
    const groupFlagged = groupFlaggedQuestions.value

    if (!groupFlagged?.length) {
        return flaggedSubjects
    }

    groupFlagged.forEach(q => {
        if (!flaggedSubjects.length) {
            const rowSubject = {
                id: Math.random(),
                subject: q.subject,
                numFlaggedSubject: 1,
                tabIndex: 0,
            }
            flaggedSubjects.push(rowSubject)
        } else if (flaggedSubjects.length) {
            const index = flaggedSubjects.findIndex(question => question.subject === q.subject)
            if (index === -1) {
                const rowSubject = {
                    id: Math.random(),
                    subject: q.subject,
                    numFlaggedSubject: 1,
                    tabIndex: 0,
                }
                flaggedSubjects.push(rowSubject)
            } else if (index >= 0) {
                const subject = flaggedSubjects[index]
                if (subject) {
                    subject.numFlaggedSubject++
                }
            }
        }
    })

    flaggedSubjects.sort((a, b) => a.numFlaggedSubject > b.numFlaggedSubject ? -1 : 1)

    return flaggedSubjects
})

const route = useRoute()
const router = useRouter()

const viewAllFlaggedQuestions = () => {
    router.push({ name: 'group-content', query: { tab: 'flagged' } })

    analyticsModule.actions.amplitudeTrack('Button Clicked', {
        buttonContext: 'Group Stats Flagged Questions View All Button',
    })
}

const rowClicked = (row: { subject: string }) => {
    router.push({ name: 'group-content', query: { subject: row.subject, tab: 'flagged' } })
    analyticsModule.actions.amplitudeTrack('List Item Clicked', {
        listItemContext: 'Group Stats Flagged Subject',
        listItemValue: row.subject,
    })
}

onMounted(async () => {
    const routeGroupId = route.params.groupId
    await Promise.all([
        groupModule.actions.updateCurrentGroupId(routeGroupId as string),
        studentModule.actions.updateCurrentStudentId(null),
    ])
})
</script>

<template>
    <div class="group-subject-table">
        <div
            v-if="flaggedGroupSubjects.length"
            class="group-subject-table__group-flagged-subjects"
        >
            <Table
                class="group-subject-table__subject-flagged-table"
                theme="open"
                :rows="flaggedGroupSubjects"
                :columns="subjectTableColumns"
                :show-header="false"
                :table-content-styles="{
                    boxSizing: 'none',
                }"
                :table-column-labels-styles="{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: '12px',
                    color: '#587183',
                    letterSpacing: '0.22px',
                    lineHeight: '14px',
                    paddingRight: '14px',
                }"
                :table-row-styles="{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '12px 0 0 12px',
                    cursor: 'pointer',
                }"
                @rowClicked="rowClicked({subject: $event.subject as string})"
            >
                <template #tableCellValue="{ row, column }">
                    <GroupSubjectRow
                        v-if="flaggedGroupSubjects.length"
                        class="group-subject-table__flagged-subject-row"
                        :row="row"
                        :column="column"
                    />
                </template>
            </Table>
            <PocketButton
                class="group-subject-table__view-flagged-questions"
                type="secondary"
                @click="viewAllFlaggedQuestions"
            >
                View All Flagged Questions
            </PocketButton>
        </div>
        <div
            v-if="!flaggedGroupSubjects.length"
            class="group-subject-table__no-flagged-header"
        >
            <div>
                Subject
            </div>
            <div>
                No. of Students Flagged
            </div>
        </div>
        <div
            v-if="!flaggedGroupSubjects.length"
            class="group-subject-table__no-flagged-subjects"
        >
            <EmptyState
                class="group-subject-table__empty-state"
                state="flagged"
                title="0 Flagged Questions"
                message="Once students flag questions you will be able to see them here."
            />
        </div>
    </div>
</template>

<style lang="scss" scoped>
.group-subject-table {
    &__no-flagged-header {
        display: flex;
        justify-content: space-between;
        font-size: 12px;
        color: $slate;
        letter-spacing: 0.22px;
        line-height: 14px;
        padding-left: 12px;
        padding-right: 12px;
    }

    &__view-flagged-questions {
        padding: 16px 12px;
        font-size: 15px;
        line-height: 18px;
    }

    &__no-flagged-subjects {
        margin: auto;
        text-align: center;
        margin-bottom: 140px;
    }

    &__empty-state {
        margin: 32px auto 14px auto;
    }
}
</style>
